import React, { useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiMail,
  FiMessageSquare,
  FiSend,
  FiUser,
} from "react-icons/fi";
import {
  FaClock,
  FaEnvelopeOpenText,
  FaPaperPlane,
  FaQuestionCircle,
} from "react-icons/fa";

import { saveMessageContactForm } from "./../../services/contactformServices";
import { sendContactFormMail } from "./../../services/mailServices";
import Reveal from "../../components/Reveal/Reveal";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const FieldLabel = ({ htmlFor, children }) => {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
    >
      {children}
    </label>
  );
};

const HelpCard = ({ icon: Icon, title, text }) => {
  return (
    <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-primary">
        <Icon />
      </div>

      <h3 className="font-barlow text-lg font-bold text-white">{title}</h3>

      <p className="mt-2 text-sm leading-6 text-white/70">{text}</p>
    </div>
  );
};

export default function FormContact() {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const canSubmit = useMemo(() => {
    const { name, email, message } = formData;

    return (
      status !== "sending" &&
      name.trim().length > 1 &&
      email.trim().length > 4 &&
      message.trim().length > 4
    );
  }, [formData, status]);

  const messageLength = formData.message.trim().length;

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (status === "error") {
      setStatus("idle");
      setErrorMsg("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    setErrorMsg("");
    setStatus("sending");

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
    };

    try {
      await saveMessageContactForm(payload);
      await sendContactFormMail(payload);

      setStatus("success");
      setFormData({
        name: "",
        email: "",
        message: "",
      });

      setTimeout(() => {
        setStatus("idle");
      }, 3500);
    } catch (error) {
      console.error("Erreur formulaire contact:", error);

      setStatus("error");
      setErrorMsg(
        error?.response?.data?.message ||
          "Une erreur est survenue. Réessaie dans quelques instants."
      );
    }
  };

  return (
    <section
      id="contact"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaEnvelopeOpenText />
            Contact
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Contactez{" "}
            <span className="text-primary">le club</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Une question, une demande d’essai ou un renseignement sur les cours ?
            Laissez-nous un message, nous vous répondrons rapidement.
          </p>
        </Reveal>

        <Reveal variant="zoom" delay={120}>
          <div className="mt-14 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <div className="grid lg:grid-cols-[0.82fr_1.18fr]">
              <div className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8 lg:p-10">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.35),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_36%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/75 backdrop-blur-xl">
                    <FaPaperPlane className="text-primary" />
                    Écrivez-nous
                  </div>

                  <h3 className="mt-6 font-barlow text-3xl font-extrabold leading-tight text-white sm:text-4xl">
                    Nous sommes là pour vous renseigner.
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-white/75 sm:text-base">
                    Votre message sera transmis au club. Pensez à préciser
                    l’âge du pratiquant, la discipline souhaitée ou le créneau
                    concerné si votre demande porte sur un cours d’essai.
                  </p>

                  <div className="mt-8 grid gap-4">
                    <HelpCard
                      icon={FaQuestionCircle}
                      title="Demande d’information"
                      text="Cours, horaires, tarifs, inscription, documents ou fonctionnement du club."
                    />

                    <HelpCard
                      icon={FaClock}
                      title="Réponse rapide"
                      text="Le club revient vers vous dès que possible, généralement sous 24 à 48 heures."
                    />
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="relative bg-gradient-to-br from-white via-[#fffaf7] to-orange-50/50 p-6 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 sm:p-8 lg:p-10"
              >
                <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />

                <div className="relative">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <FieldLabel htmlFor="name">Nom / Prénom</FieldLabel>

                      <div className="relative">
                        <FiUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Votre nom et prénom"
                          autoComplete="name"
                          className={cn(
                            "h-12 w-full rounded-2xl border pl-11 pr-4 text-sm font-semibold outline-none transition",
                            "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                            "focus:border-primary focus:ring-4 focus:ring-primary/10",
                            "dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:placeholder:text-slate-500"
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <FieldLabel htmlFor="email">Votre email</FieldLabel>

                      <div className="relative">
                        <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="mail@gmail.com"
                          autoComplete="email"
                          className={cn(
                            "h-12 w-full rounded-2xl border pl-11 pr-4 text-sm font-semibold outline-none transition",
                            "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                            "focus:border-primary focus:ring-4 focus:ring-primary/10",
                            "dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:placeholder:text-slate-500"
                          )}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <FieldLabel htmlFor="message">Votre message</FieldLabel>

                        <span className="text-xs font-semibold text-slate-400">
                          {messageLength} caractère
                          {messageLength > 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="relative">
                        <FiMessageSquare className="pointer-events-none absolute left-4 top-4 text-slate-400" />

                        <textarea
                          id="message"
                          name="message"
                          rows={7}
                          required
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Votre message..."
                          className={cn(
                            "w-full resize-none rounded-2xl border py-4 pl-11 pr-4 text-sm font-semibold leading-7 outline-none transition",
                            "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                            "focus:border-primary focus:ring-4 focus:ring-primary/10",
                            "dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:placeholder:text-slate-500"
                          )}
                        />
                      </div>

                      <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        Les informations transmises servent uniquement à traiter
                        votre demande de contact.
                      </p>
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-h-[28px] text-sm">
                      {status === "success" && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 font-bold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                          <FiCheckCircle />
                          Message envoyé
                        </div>
                      )}

                      {status === "error" && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                          <FiAlertTriangle />
                          {errorMsg || "Erreur d’envoi"}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={cn(
                        "inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-sm font-bold shadow-lg transition",
                        "bg-primary text-white",
                        "hover:-translate-y-0.5 hover:brightness-110",
                        "focus:outline-none focus:ring-4 focus:ring-primary/20",
                        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:brightness-100"
                      )}
                      aria-label="Envoyer le message"
                    >
                      {status === "sending" ? (
                        <>
                          <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <FiSend className="text-lg" />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div
              aria-hidden
              className="h-1.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}